import { records as jobRecords } from '@saucelabs/sl-module-jobs';

import type {
  BacktraceInformation,
  CrashReport,
  CallstackElement,
  JobFilters,
  RequestBody,
  Report,
} from './types/errorReport.d';
import { ABORT, CRASH } from './constants';
import { APP_STORAGE_KIND } from '../../constants';

const getDeviceModel = (report: Report | null): string => {
  if (report) {
    const firstErrorType = report.values[0];

    if (firstErrorType) {
      const fold = firstErrorType[1];

      if (fold) {
        const deviceModel: Array<string> = fold[4];
        return deviceModel[0];
      }
    }
  }

  return '';
};

const getCrashReport = (report: Report | null): CrashReport | null => {
  if (!report) {
    return null;
  }

  const { values } = report;
  const crashEntry = values.find(
    (
      entry: Array<string| Array<string> | number>,
    ) => [ABORT, CRASH].includes(String(entry[0])),
  );

  if (!crashEntry) {
    return null;
  }

  const crashEntryValues = crashEntry[1];
  const fingerprint = crashEntryValues[0][0];

  const functionNames: Array<string> = JSON.parse(crashEntryValues[1]).frame;
  const fileNames: Array<string> = JSON.parse(crashEntryValues[2]).frame;
  const isSymbolMissing = Boolean(JSON.parse(crashEntryValues[3]));

  const callstack: Array<CallstackElement> = functionNames
    .map((functionName: string, index: number) => ({
      isSymbolMissing,
      functionName: functionName === '?' ? '<...>' : functionName,
      fileName: fileNames[index],
      id: index,
    }));

  return {
    fingerprint,
    callstack,
  };
};

const generateErrorReportUrl = ({
  isAndroid,
  url,
  projectName,
  deviceModel,
  packageName,
  startTime,
  endTime,
}: BacktraceInformation & {isAndroid: boolean}): string => {
  const project = `/p/${projectName}`;

  const filterStart = '/triage?time=month&filters=(';
  const filterDeviceModel = `device.model%3D${deviceModel}%2C`;

  const filterApplication = isAndroid ?
    `application.package%3D${packageName}%2C` :
    `application.identifier%3D${packageName}%2C`;

  const filterStartTime = `timestamp%3E%3D%22${startTime}%22%2C`;
  const filterEndTime = `timestamp%3C%3D%22${endTime}%22)`;

  return url + project + filterStart + filterDeviceModel +
    filterApplication + filterStartTime + filterEndTime;
};

const generateCrashReportUrl = ({ url, projectName, fingerprint }: {
  url: string;
  projectName: string;
  fingerprint: string;
}): string => {
  const projectPart = `/p/${projectName}`;
  const filterPart = `/debug?time=month&fingerprint=${fingerprint}`;

  return url + projectPart + filterPart;
};

const transformDataForIosAppReport = (data: JobFilters): RequestBody => ({
  group: ['error.type'],
  fold: {
    fingerprint: [
      [
        'head',
      ],
    ],
    'callstack.functions': [
      [
        'head',
      ],
    ],
    'callstack.modules': [
      [
        'head',
      ],
    ],
    _missing_symbol: [
      [
        'head',
      ],
    ],
    'device.model': [
      [
        'head',
      ],
    ],
  },
  filter: [
    {
      ...(data.packageName ? {
        'application.identifier': [
          [
            'equal',
            data.packageName,
          ],
        ],
      } : {}),
      'device.machine': [
        [
          'contains',
          data.modelNumber,
        ],
      ],
      timestamp: [
        [
          'at-most',
          data.endTime,
        ],
        [
          'at-least',
          data.startTime,
        ],
      ],
    },
  ],
});

const transformDataForAndroidAppReport = (data: JobFilters): RequestBody => ({
  group: ['error.type'],
  fold: {
    fingerprint: [
      [
        'head',
      ],
    ],
    'callstack.functions': [
      [
        'head',
      ],
    ],
    'callstack.modules': [
      [
        'head',
      ],
    ],
    _missing_symbol: [
      [
        'head',
      ],
    ],
    'device.model': [
      [
        'head',
      ],
    ],
  },
  filter: [{
    ...(data.packageName ? {
      'application.package': [
        [
          'equal',
          data.packageName,
        ],
      ],
    } : {}),
    'device.model': [
      [
        'contains',
        data.modelNumber,
      ],
    ],
    timestamp: [
      [
        'at-most',
        data.endTime,
      ],
      [
        'at-least',
        data.startTime,
      ],
    ],
  }],
});

const generateAttributeSettingsUrl = (url: string, projectName: string) => `${url}/p/${projectName}/settings/attributes`;

const generateDashboardUrl = (url: string, projectName: string) => `${url}/p/${projectName}`;

const checkIsAndroid = (job: jobRecords.JobRecord) => (
  job.os ?
    APP_STORAGE_KIND.ANDROID === job.os.toLowerCase() :
    job.automation_backend === 'espresso'
);

export {
  generateCrashReportUrl,
  generateErrorReportUrl,
  generateAttributeSettingsUrl,
  generateDashboardUrl,
  getCrashReport,
  getDeviceModel,
  transformDataForAndroidAppReport,
  transformDataForIosAppReport,
  checkIsAndroid,
};
